import { useState, useEffect, useRef } from 'react';
import { Send } from 'lucide-react';
import { MessageType, type InstanceMessage, type SendMessageDto } from '../types/team';

const MESSAGE_TYPE_STYLES: Record<MessageType, string> = {
  [MessageType.TEXT]: 'bg-slate-50',
  [MessageType.TASK_ASSIGNMENT]: 'bg-blue-50',
  [MessageType.STATUS_UPDATE]: 'bg-green-50',
  [MessageType.SHUTDOWN_REQUEST]: 'bg-red-50',
};

const MESSAGE_TYPE_LABELS: Record<MessageType, string> = {
  [MessageType.TEXT]: '',
  [MessageType.TASK_ASSIGNMENT]: 'Task Assignment',
  [MessageType.STATUS_UPDATE]: 'Status Update',
  [MessageType.SHUTDOWN_REQUEST]: 'Shutdown Request',
};

function formatTime(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

interface TeamMessagesProps {
  messages: InstanceMessage[];
  instanceOptions?: { id: string; name: string }[];
  onSendMessage?: (data: SendMessageDto) => void;
  isSending?: boolean;
}

export default function TeamMessages({ messages, instanceOptions, onSendMessage, isSending }: TeamMessagesProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [autoScroll, setAutoScroll] = useState(true);
  const [content, setContent] = useState('');
  const [fromId, setFromId] = useState('');
  const [toId, setToId] = useState('');

  useEffect(() => {
    if (autoScroll && containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [messages, autoScroll]);

  const handleSend = () => {
    if (!content.trim() || !fromId || !onSendMessage) return;
    onSendMessage({
      fromInstanceId: fromId,
      toInstanceId: toId || undefined,
      content: content.trim(),
    });
    setContent('');
  };

  return (
    <div className="flex flex-col rounded-lg border border-slate-200 bg-white">
      <div className="flex items-center justify-between border-b border-slate-200 px-4 py-2">
        <span className="text-sm font-medium text-slate-700">Messages</span>
        <label className="flex items-center gap-1.5 text-xs text-slate-400">
          <input
            type="checkbox"
            checked={autoScroll}
            onChange={(e) => setAutoScroll(e.target.checked)}
            className="rounded border-slate-300"
          />
          Auto-scroll
        </label>
      </div>

      <div ref={containerRef} className="flex-1 overflow-y-auto p-4" style={{ maxHeight: '400px', minHeight: '200px' }}>
        {messages.length === 0 ? (
          <div className="py-8 text-center text-sm text-slate-400">No messages yet</div>
        ) : (
          <div className="space-y-3">
            {messages.map((msg) => {
              const isBroadcast = !msg.toInstanceId;
              const bgStyle = MESSAGE_TYPE_STYLES[msg.messageType] ?? 'bg-slate-50';
              const typeLabel = MESSAGE_TYPE_LABELS[msg.messageType];

              return (
                <div key={msg.id} className={`rounded-lg p-3 ${bgStyle}`}>
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-slate-700">
                      {msg.fromInstance?.agent?.name ?? 'Unknown'}
                    </span>
                    <span className="text-slate-400">
                      {isBroadcast ? '(broadcast)' : `to ${msg.toInstance?.agent?.name ?? 'Unknown'}`}
                    </span>
                    {typeLabel && (
                      <span className="rounded bg-white/60 px-1.5 py-0.5 text-xs text-slate-500">
                        {typeLabel}
                      </span>
                    )}
                    <span className="ml-auto text-slate-400">{formatTime(msg.createdAt)}</span>
                  </div>
                  <p className="mt-1.5 whitespace-pre-wrap text-sm text-slate-600">{msg.content}</p>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {onSendMessage && instanceOptions && instanceOptions.length > 0 && (
        <div className="border-t border-slate-200 p-3">
          <div className="mb-2 flex gap-2">
            <select
              value={fromId}
              onChange={(e) => setFromId(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-slate-500 focus:outline-none"
            >
              <option value="">From...</option>
              {instanceOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
            <select
              value={toId}
              onChange={(e) => setToId(e.target.value)}
              className="rounded-lg border border-slate-300 bg-white px-2.5 py-1.5 text-xs text-slate-700 focus:border-slate-500 focus:outline-none"
            >
              <option value="">Broadcast</option>
              {instanceOptions.map((opt) => (
                <option key={opt.id} value={opt.id}>{opt.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-2">
            <input
              type="text"
              value={content}
              onChange={(e) => setContent(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Type a message..."
              className="flex-1 rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-900 focus:border-slate-500 focus:outline-none focus:ring-1 focus:ring-slate-500"
            />
            <button
              onClick={handleSend}
              disabled={!content.trim() || !fromId || isSending}
              className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-xs font-medium text-white hover:bg-slate-800 disabled:opacity-50"
            >
              <Send className="h-3.5 w-3.5" />
              Send
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
